import React from "react";
import { useAnalysis } from "../context/AnalysisContext";

const Analysis: React.FC = () => {
  const { text, setText, file, setFile, loading, result, error, handleClear, handleSubmit } = useAnalysis();

  const isTextActive = text.trim().length > 0;
  const isFileActive = file !== null;

  const models = ["bkmx", "olda", "finbert"];

  const scores = result?.results ?? {};

  const summaries = result?.summary ?? {};

  const topics = result?.topics ?? {};

  const getDynamicColor = (score: number) => {
    const intensity = Math.min(Math.abs(score) * 100, 100);

    if (score > 0) {
      return `hsl(142, ${70 + intensity * 0.3}%, ${45 - intensity * 0.1}%)`;
    } else if (score < 0) {
      return `hsl(0, ${70 + intensity * 0.3}%, ${45 - intensity * 0.1}%)`;
    }
    return "inherit";
  };

  return (
    <>
      <div className="max-w-4xl mx-auto mb-6 text-center text-gray-600 dark:text-slate-100">
        <p>
          Paste your earnings report or upload a PDF file. Our AI models will return a score,
          extract key topics, and provide a summary of the content and the analysis.
        </p>
      </div>
      <div className="w-full mx-auto p-6 bg-white dark:bg-slate-900 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mb-4">
          Upload Your Earnings Report
        </h2>

        <div className="flex flex-col md:flex-row gap-6 w-full items-start">
          <div className={`flex-1 w-full transition-opacity duration-300 ${isFileActive ? 'opacity-40' : 'opacity-100'}`}>
            <label className="block text-sm font-semibold mb-2 dark:text-slate-300">
              Paste Earnings Report
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={isFileActive}
              placeholder={isFileActive ? "You can only upload one input type at a time" : "Paste your earnings report here..."}
              className="w-full h-80 p-3 dark:bg-slate-900 border border-gray-300 dark:border-slate-800 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:cursor-not-allowed shadow-sm"
            />
          </div>

          <div className={`flex-1 w-full flex flex-col transition-opacity duration-300 ${isTextActive ? 'opacity-40' : 'opacity-100'}`}>
            <label className="block text-sm font-semibold mb-2 dark:text-slate-300">
              Upload Earnings Report PDF
            </label>
            <div className={`w-full h-80 border-2 border-dashed rounded-md flex flex-col items-center justify-center p-6 transition-colors
              ${isTextActive ? 'border-gray-300 bg-gray-50 cursor-not-allowed' : 'border-blue-400 bg-blue-50/30 dark:bg-slate-800/50'}`}>
              
              <input 
                type="file" 
                accept="application/pdf" 
                className="hidden" 
                id="pdf-upload" 
                disabled={isTextActive}
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setFile(e.target.files[0]);
                  }
                }}
              />
              
              {!file ? (
                <label 
                  htmlFor="pdf-upload" 
                  className={`font-semibold ${isTextActive ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-500 cursor-pointer'}`}
                >
                  {isTextActive ? "You can only upload one input type at a time" : "Click to upload PDF"}
                </label>
              ) : (
                <div className="text-center">
                  <p className="text-sm font-medium text-green-600">Selected: {file.name}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <button
          disabled={(!text.trim() && !file) || loading}
          onClick={handleClear}
          className="mt-4 w-full flex items-center justify-center bg-red-600 text-white font-bold py-2 px-4 rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          Clear Input
        </button>

        <button
          disabled={(!text.trim() && !file) || loading}
          onClick={handleSubmit}
          className="mt-2 w-full flex items-center justify-center bg-blue-600 text-white font-bold py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {loading && (
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white dark:text-slate-300"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          )}
          {loading ? "Submitting..." : "Submit Input"}
        </button>

        {error && (
          <div className="mt-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md">
            <p>
              <span className="font-bold">Error:</span> {error}
            </p>
          </div>
        )}

        {(loading || result) && (
          <div className="mt-6 space-y-8 animate-in fade-in duration-500">
            <div className="p-6 bg-indigo-50/50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-2xl">
              <div className="flex items-center gap-2 mb-3">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500"></span>
                </span>
                <h3 className="text-sm font-bold uppercase tracking-widest text-indigo-700 dark:text-indigo-400">
                  GPT-5 Summary
                </h3>
              </div>
              <p className="text-gray-700 dark:text-slate-300 leading-relaxed italic">
                {summaries.gpt_summary || "Loading..."}
              </p>
            </div>

            <div className="flex gap-4">
              {["bkmx", "olda", "finbert"].map((model) => (
                <div
                  key={model}
                  className="flex-1 p-4 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-lg text-center shadow-sm"
                >
                  <div className="text-xs font-bold uppercase text-gray-400 mb-1">
                    {model}
                  </div>
                  <div
                    className="text-2xl font-bold"
                    style={{ color: getDynamicColor(scores[model] || 0) }}
                  >
                    {(scores[model] || 0).toFixed(4)}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {models.map((m) => (
                <div key={m} className="flex flex-col">
                  <h4 className="text-xs font-bold text-gray-400 dark:text-slate-500 uppercase mb-3 px-1">
                    {m} Key Topics
                  </h4>
                  <div className="flex flex-wrap items-start content-start gap-2 p-4 bg-gray-50 dark:bg-slate-900/50 border border-gray-100 dark:border-slate-800 rounded-xl min-h-[120px]">
                    {topics[m]?.length > 0 ? (
                      topics[m].map((topic: string, i: number) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-full text-xs font-medium text-gray-600 dark:text-slate-300 shadow-sm"
                        >
                          {topic}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-400 italic">
                        No topics
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {models.map((m) => {
                const rawText = summaries[m] || "";
                const actionMatch = rawText.match(/ACTION:\s*(.*?)(?=\s*SUMMARY:|$)/i);
                const summaryMatch = rawText.match(/SUMMARY:\s*([\s\S]*)/i);

                const action = actionMatch ? "ACTION: " + actionMatch[1].trim() : "";
                const summary = summaryMatch ? "SUMMARY: " + summaryMatch[1].trim() : "";
                return (
                  <div
                    key={m}
                    className="p-5 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 rounded-xl shadow-sm"
                  >
                    <div className="text-xs font-bold text-emerald-600 dark:text-emerald-500 uppercase mb-3">
                      {m} Summary
                    </div>
                    <div className="text-sm text-gray-600 dark:text-slate-400 whitespace-pre-wrap leading-relaxed">
                      {action || "Loading..."}
                    </div>
                    <div className="text-sm text-left text-gray-600 dark:text-slate-400 whitespace-pre-wrap leading-relaxed">
                      {summary || ""}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Analysis;
